import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Star, Share2, Download, Trash2, Volume2, VolumeX, Play, Pause, Eye, EyeOff } from 'lucide-react';
import { useShareSettings } from '../../utils/shareSettings';
import { absoluteUrl } from '../../utils/absoluteUrl';

/**
 * Video Fullscreen - Design wie ImageActionSheet
 */
const VideoActionSheet = ({
  isOpen,
  onClose,
  video,
  videos = [],
  initialIndex = 0,
  onToggleFavorite,
  onDelete,
  favorites = new Set(),
}) => {
  const shareSettings = useShareSettings();
  const scrollYRef = useRef(0);
  const closingRef = useRef(false);
  const chipsRef = useRef(null);

  // 3 VIDEO REFS - TikTok Style!
  const videoRefs = useRef([null, null, null]); // [prev, current, next]
  const videoRef = useRef(null); // Shortcut für aktuelles

  // SIMPEL: Aktuelles Video als State, startet mit prop
  const [currentVideo, setCurrentVideo] = useState(video);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [localVideos, setLocalVideos] = useState(videos); // Lokale Kopie für Delete
  const [chipsExpanded, setChipsExpanded] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(() => {
    try { return localStorage.getItem('snapshot_video_muted') === '1'; } catch { return false; }
  });
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const volumeHideTimeout = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(!video?.preloaded); // Kein Spinner wenn preloaded // Spinner beim Start

  // Desktop Check
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;

  // Landscape-Erkennung: Handy kippen → CSS-Fullscreen (kein natives Fullscreen!)
  // Video spielt durch, UI wird aus-/eingeblendet, kein Pause
  const [isLandscapeFullscreen, setIsLandscapeFullscreen] = useState(false);
  useEffect(() => {
    if (!isOpen || isDesktop) return;

    const handleOrientation = () => {
      setTimeout(() => {
        const isLandscape = window.innerWidth > window.innerHeight;
        setIsLandscapeFullscreen(isLandscape);
      }, 150);
    };

    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleOrientation);
    }
    window.addEventListener('resize', handleOrientation);

    // Initial check
    handleOrientation();

    return () => {
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleOrientation);
      }
      window.removeEventListener('resize', handleOrientation);
      setIsLandscapeFullscreen(false);
    };
  }, [isOpen, isDesktop]);

  // localVideos NUR beim ÖFFNEN initialisieren, danach nie mehr von außen überschreiben!
  const initializedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      setLocalVideos(videos);
      initializedRef.current = true;
    }
    if (!isOpen) {
      initializedRef.current = false; // Reset beim Schließen
    }
  }, [isOpen, videos]);

  // Anzahl Videos für Navigation (aus lokalem State!)
  const totalVideos = localVideos.length > 0 ? localVideos.length : 1;

  // Beim Öffnen: Video und Index setzen
  useEffect(() => {
    if (isOpen && video) {
      console.log('🎬 VideoActionSheet opened with:', video);
      setCurrentVideo(video);
      // Index finden - berücksichtige Wrapper-Struktur
      if (videos.length > 0) {
        const idx = videos.findIndex(v => {
          const vData = getVideoData(v);
          return vData?.id === video.id || vData?.video_url === video.video_url;
        });
        setCurrentIndex(idx >= 0 ? idx : 0);
      } else {
        setCurrentIndex(0);
      }
      setChipsExpanded(true);
      setDetailsExpanded(false);
      setPlaybackSpeed(1);
      setIsPlaying(true);
      try { setIsMuted(localStorage.getItem('snapshot_video_muted') === '1'); } catch { setIsMuted(false); }
    }
  }, [isOpen, video?.id]);

  const isFavorite = currentVideo && favorites.has(currentVideo.id);
  const canGoPrev = localVideos.length > 1 && currentIndex > 0;
  const canGoNext = localVideos.length > 1 && currentIndex < localVideos.length - 1;

  // Navigation - Videos können direkt oder als Wrapper ({type, data}) kommen
  const getVideoData = (v) => {
    if (!v) return null;
    // Wenn es ein Wrapper ist ({type: 'video', data: {...}})
    if (v.type === 'video' && v.data) return v.data;
    // Sonst direkt
    return v;
  };

  const goToVideo = (newIndex) => {
    if (localVideos.length > 0 && newIndex >= 0 && newIndex < localVideos.length) {
      const rawVideo = localVideos[newIndex];
      const videoData = getVideoData(rawVideo);

      if (videoData && videoData.video_url) {
        // Aktuelles Video pausieren
        if (videoRef.current) {
          videoRef.current.pause();
        }

        setCurrentIndex(newIndex);
        setCurrentVideo(videoData);
        setPlaybackSpeed(1);
        setIsPlaying(true);
        setCurrentTime(0);
        setDuration(0);

        // Video direkt starten - moov atom am Anfang = sofort spielbar
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.load();
            videoRef.current.play().catch(() => {});
          }
        }, 0);
      }
    }
  };

  const showToast = (msg) => {
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-24 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-md text-white rounded-full text-sm z-[1000001]';
    toast.textContent = msg;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  };

  const handleClose = useCallback((e) => {
    if (e) e.preventDefault(); // Verhindert Browser-Navigation
    if (closingRef.current || isDeleting) return;
    closingRef.current = true;

    const videoId = currentVideo?.id;

    // WICHTIG: Video stoppen und src leeren BEVOR State gelöscht wird
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
      videoRef.current.load(); // Cleanup
    }

    setTimeout(() => {
      onClose(videoId);
      closingRef.current = false;
    }, 50);
  }, [onClose, isDeleting, currentVideo?.id]);

  // Video-Daten für Navigation
  const getVideoAtIndex = (idx) => {
    if (idx < 0 || idx >= localVideos.length) return null;
    return getVideoData(localVideos[idx]);
  };

  const prevVideo = getVideoAtIndex(currentIndex - 1);
  const nextVideo = getVideoAtIndex(currentIndex + 1);

  // Video beim Öffnen starten — immer mit Sound versuchen
  useEffect(() => {
    if (isOpen && videoRef.current && currentVideo?.video_url) {
      const timer = setTimeout(() => {
        if (!videoRef.current) return;
        // Immer mit Sound starten (User-Entscheidung respektieren)
        const userWantsMuted = localStorage.getItem('snapshot_video_muted') === '1';
        videoRef.current.muted = userWantsMuted;
        setIsMuted(userWantsMuted);

        videoRef.current.play().catch(() => {
          console.log('🔇 Autoplay blocked by browser, trying muted...');
          if (videoRef.current) {
            // Safari/iOS: Autoplay nur muted erlaubt
            videoRef.current.muted = true;
            setIsMuted(true);
            videoRef.current.play().catch(() => {});
            // Bei nächster User-Interaktion unmuten (einmalig)
            const unmute = () => {
              if (videoRef.current && !userWantsMuted) {
                videoRef.current.muted = false;
                setIsMuted(false);
              }
              document.removeEventListener('touchstart', unmute);
              document.removeEventListener('click', unmute);
            };
            document.addEventListener('touchstart', unmute, { once: true });
            document.addEventListener('click', unmute, { once: true });
          }
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentVideo?.video_url]);

  // Cleanup beim Unmount - verhindert Crash
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
      }
    };
  }, []);

  // Preload nächstes/vorheriges Video im Hintergrund
  useEffect(() => {
    if (!isOpen) return;
    const links = [];
    [prevVideo, nextVideo].forEach(v => {
      if (v?.video_url) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'video';
        link.href = v.video_url;
        document.head.appendChild(link);
        links.push(link);
      }
    });
    return () => { links.forEach(l => l.remove()); };
  }, [isOpen, currentIndex]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    scrollYRef.current = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
    closingRef.current = false;

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isOpen]);

  // Video controls
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const speedButtonsRef = useRef(null);

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (videoRef.current) videoRef.current.playbackRate = speed;
    // Direktes DOM-Update für Speed-Buttons
    if (speedButtonsRef.current) {
      const buttons = speedButtonsRef.current.querySelectorAll('button');
      buttons.forEach(btn => {
        const btnSpeed = parseFloat(btn.dataset.speed);
        if (btnSpeed === speed) {
          btn.style.backgroundColor = 'rgba(34, 197, 94, 0.3)'; // green-500/30
          btn.style.color = 'rgba(34, 197, 94, 1)';
        } else {
          btn.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          btn.style.color = 'rgba(255, 255, 255, 0.8)';
        }
      });
    }
  };

  const soundButtonRef = useRef(null);

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !videoRef.current.muted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
      try { localStorage.setItem('snapshot_video_muted', newMuted ? '1' : '0'); } catch {}
      // Direktes DOM-Update für Sound-Icon
      if (soundButtonRef.current) {
        const icon = soundButtonRef.current.querySelector('svg');
        if (icon) {
          // Wechsel zwischen VolumeX und Volume2 durch Opacity
          soundButtonRef.current.dataset.muted = newMuted ? 'true' : 'false';
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (!currentVideo || !videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!currentVideo || !videoRef.current) return;
    setDuration(videoRef.current.duration);
    videoRef.current.playbackRate = playbackSpeed;
  };

  const isScrubbing = useRef(false);
  const timelineRef = useRef(null);

  const seekToX = useCallback((clientX) => {
    if (!timelineRef.current || !videoRef.current || !duration) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const newTime = percent * duration;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime); // Sofort State updaten — Handle folgt live
  }, [duration]);

  // Alle Touch- und Mouse-Events direkt registrieren (passive: false für Touch-Drag)
  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;

    const onStart = (e) => {
      isScrubbing.current = true;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      seekToX(x);
    };

    const onMove = (e) => {
      if (!isScrubbing.current) return;
      e.preventDefault(); // Verhindert Page-Scroll beim Scrubben
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      seekToX(x);
    };

    const onEnd = () => { isScrubbing.current = false; };

    // Touch-Events mit passive: false — damit preventDefault() funktioniert
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);

    // Mouse-Events
    el.addEventListener('mousedown', onStart);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('mousedown', onStart);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
    };
  }, [seekToX]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Kein Proxy — Videos liegen lokal und werden direkt ausgeliefert
  const getProxyUrl = (url) => url || '';

  // Actions
  const handleShare = async () => {
    if (!currentVideo || isSharing) return; // Doppelklick-Schutz
    setIsSharing(true);
    const shareText = shareSettings.video_share_text || 'Mein KI-Video — Erstellt mit SnapshotPro.ai';
    try {
      showToast('Video wird vorbereitet...');
      const { shareVideo } = await import('../../utils/shareMedia');
      const result = await shareVideo({
        videoUrl: currentVideo.video_url,
        text: shareText,
        posterUrl: currentVideo.poster_thumbnail_url || currentVideo.thumbnail_url,
      });
      if (result === 'clipboard') showToast('Video-Link kopiert!');
    } catch (err) {
      if (err && err.name !== 'AbortError') {
        console.error('Share error:', err);
        showToast('Teilen fehlgeschlagen');
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleDownload = async () => {
    if (!currentVideo || isDownloading) return;
    setIsDownloading(true);
    try {
      showToast('Download wird vorbereitet...');
      const downloadUrl = getProxyUrl(currentVideo.video_url);
      const response = await fetch(downloadUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Fetch failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SnapshotPro-Video-${currentVideo.id || Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Download gestartet...');
    } catch (err) {
      console.log('CORS download failed, opening in new tab');
      window.open(getProxyUrl(currentVideo.video_url), '_blank');
      showToast('Video wird in neuem Tab geöffnet — lange drücken zum Speichern');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFavorite = () => {
    if (onToggleFavorite && currentVideo) onToggleFavorite(currentVideo.id);
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    if (!currentVideo || isDeleting) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    if (!currentVideo || isDeleting) return;
    setIsDeleting(true);
    try {
      if (onDelete) await onDelete(currentVideo);
      const newVideos = localVideos.filter((_, idx) => idx !== currentIndex);

      if (newVideos.length === 0) {
        showToast('Video gelöscht');
        setIsDeleting(false);
        handleClose();
      } else {
        // Neuen Index berechnen
        const newIndex = currentIndex >= newVideos.length ? newVideos.length - 1 : currentIndex;

        // Nächstes Video laden
        const nextVideo = newVideos[newIndex];
        const nextVideoData = getVideoData(nextVideo);

        setLocalVideos(newVideos);
        setCurrentIndex(newIndex);
        if (nextVideoData) {
          setCurrentVideo(nextVideoData);
          // Video-Element neu laden
          setTimeout(() => {
            if (videoRef.current) {
              videoRef.current.load();
              videoRef.current.play().catch(() => {});
            }
          }, 100);
        }

        showToast('Video gelöscht');
        setIsDeleting(false);
      }
    } catch (err) {
      showToast('Löschen fehlgeschlagen');
      setIsDeleting(false);
    }
  };

  // Toggle Chips (DOM-basiert wegen iOS Safari Bug)
  const toggleChipsMenu = (e) => {
    if (e) e.preventDefault();
    if (!chipsRef.current) return;
    const isCurrentlyOpen = chipsRef.current.style.maxHeight !== '0px';
    const newState = !isCurrentlyOpen;
    setChipsExpanded(newState);
    chipsRef.current.style.maxHeight = newState ? (detailsExpanded ? '800px' : '400px') : '0px';
    chipsRef.current.style.opacity = newState ? '1' : '0';
  };

  if (!isOpen || !currentVideo) return null;

  const content = (
    <div
      data-video-viewer
      className="fixed inset-0 bg-black"
      style={{
        zIndex: 999999,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        overscrollBehavior: 'none',
      }}
    >
      {console.log("🎬 VideoActionSheet currentVideo:", currentVideo)}
      {/* VIDEO - Fullscreen (KEIN Poster, nur Video!) */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <video
          ref={videoRef}
          src={currentVideo.video_url}
          preload="metadata"
          autoPlay
          loop
          playsInline
          muted={isMuted}
          className={`select-none transition-all duration-300 ${isLandscapeFullscreen ? 'w-full h-full object-contain' : 'max-w-full max-h-full object-contain'}`}
          style={{ pointerEvents: 'none' }}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onCanPlay={() => setIsLoading(false)}
          onLoadStart={() => !currentVideo?.preloaded && setIsLoading(true)}
        />

        {/* LOADING SPINNER - auf dem Poster */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* NAVIGATION ARROWS - dezent, KEIN Effekt, stillstehend */}
      {canGoPrev && (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            goToVideo(currentIndex - 1);
          }}
          className="absolute left-3 top-1/2 w-11 h-11 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center z-10 cursor-pointer"
          style={{
            transform: 'translateY(-50%)',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none'
          }}
        >
          <ChevronLeft className="w-6 h-6 text-white/80 pointer-events-none" />
        </div>
      )}
      {canGoNext && (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            goToVideo(currentIndex + 1);
          }}
          className="absolute right-3 top-1/2 w-11 h-11 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center z-10 cursor-pointer"
          style={{
            transform: 'translateY(-50%)',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none'
          }}
        >
          <ChevronRight className="w-6 h-6 text-white/80 pointer-events-none" />
        </div>
      )}

      {/* Sound Toggle ist jetzt in der Speed-Buttons-Zeile integriert */}

      {/* UI OVERLAY — ausgeblendet bei Landscape-Fullscreen */}
      <div className={`absolute inset-0 flex flex-col pointer-events-none transition-opacity duration-300 ${isLandscapeFullscreen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>

        {/* Header */}
        <div className="p-3 pt-safe flex items-center justify-between pointer-events-auto">
          <button
            type="button"
            onClick={handleClose}
            disabled={isDeleting}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center active:scale-95"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>

          <div className="flex-1" />

          {onToggleFavorite && (
            <button
              type="button"
              onClick={handleFavorite}
              className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center active:scale-95"
            >
              <Star className={`w-5 h-5 ${isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-white'}`} />
            </button>
          )}
        </div>

        {/* Spacer - Play/Pause */}
        <div className="flex-1 pointer-events-auto cursor-pointer" onClick={togglePlay} />

        {/* Bottom Section - mit edlem Gradient */}
        <div
          className="pointer-events-auto"
          style={{
            background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.85) 100%)',
          }}
        >

          {/* Expandable Actions */}
          <div
            ref={chipsRef}
            className="overflow-hidden overflow-y-auto transition-all duration-300 ease-out px-3 pt-6"
            style={{ maxHeight: chipsExpanded ? (detailsExpanded ? '60vh' : '400px') : '0px', opacity: chipsExpanded ? '1' : '0' }}
          >
            {/* Speed Controls + Volume — mobil linksbündig, Desktop zentriert */}
            <div ref={speedButtonsRef} className="relative flex items-center gap-2 mb-3 justify-start sm:justify-center">
              {[0.5, 1, 1.25, 1.5, 2].map(speed => (
                <button
                  key={speed}
                  data-speed={speed}
                  onTouchEnd={(e) => { e.preventDefault(); handleSpeedChange(speed); }}
                  onClick={() => handleSpeedChange(speed)}
                  className="px-2.5 py-2 sm:px-3 rounded-xl text-sm font-medium backdrop-blur-sm"
                  style={{
                    backgroundColor: speed === 1 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                    color: speed === 1 ? 'rgba(34, 197, 94, 1)' : 'rgba(255, 255, 255, 0.8)',
                  }}
                >
                  {speed}x
                </button>
              ))}
              {/* Sound Controls — absolute rechts */}
              <div
                className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-2"
                style={{ zIndex: 1000000 }}
              >
                {/* Desktop: Volume Slider — immer sichtbar */}
                {isDesktop && (
                  <div className="flex rounded-xl p-2 items-center gap-2 w-[140px] border border-white/10"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <VolumeX className="w-4 h-4 text-white/40 flex-shrink-0" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      defaultValue={videoRef.current?.volume ?? 1}
                      onChange={(e) => {
                        if (videoRef.current) {
                          const vol = parseFloat(e.target.value);
                          videoRef.current.volume = vol;
                          videoRef.current.muted = vol === 0;
                          setIsMuted(vol === 0);
                          try { localStorage.setItem('snapshot_video_muted', vol === 0 ? '1' : '0'); } catch {}
                        }
                      }}
                      className="w-full h-1 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_6px_rgba(255,255,255,0.4)]"
                    />
                    <Volume2 className="w-4 h-4 text-white/40 flex-shrink-0" />
                  </div>
                )}
                {/* Mute Button — Desktop + Mobil */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (videoRef.current) {
                      const newMuted = !videoRef.current.muted;
                      videoRef.current.muted = newMuted;
                      setIsMuted(newMuted);
                      if (!newMuted && videoRef.current.volume === 0) videoRef.current.volume = 0.5;
                      try { localStorage.setItem('snapshot_video_muted', newMuted ? '1' : '0'); } catch {}
                    }
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (videoRef.current) {
                      const newMuted = !videoRef.current.muted;
                      videoRef.current.muted = newMuted;
                      setIsMuted(newMuted);
                      if (!newMuted && videoRef.current.volume === 0) videoRef.current.volume = 0.5;
                      try { localStorage.setItem('snapshot_video_muted', newMuted ? '1' : '0'); } catch {}
                    }
                  }}
                  className="w-10 h-10 rounded-xl backdrop-blur-md flex items-center justify-center border border-white/10"
                  style={{ backgroundColor: isMuted ? 'rgba(255, 255, 255, 0.08)' : 'rgba(16, 185, 129, 0.3)' }}
                >
                  {isMuted
                    ? <VolumeX className="w-5 h-5 text-white/70" />
                    : <Volume2 className="w-5 h-5 text-white/70" />
                  }
                </button>
              </div>
            </div>

            {/* Action Buttons - Download + Teilen + Details + Löschen */}
            <div className="grid grid-cols-4 gap-2 mb-2">
              <ActionButton
                icon={Download}
                label={isDownloading ? 'Lädt …' : 'Download'}
                onClick={handleDownload}
                loading={isDownloading}
              />
              <ActionButton
                icon={Share2}
                label={isSharing ? 'Wird geteilt …' : 'Teilen'}
                onClick={handleShare}
                loading={isSharing}
              />
              <ActionButton
                icon={detailsExpanded ? EyeOff : Eye}
                label="Details"
                onTouchEnd={(e) => {
                  e.preventDefault();
                  const next = !detailsExpanded;
                  setDetailsExpanded(next);
                  if (chipsRef.current) chipsRef.current.style.maxHeight = next ? '800px' : '400px';
                }}
                onClick={() => {
                  const next = !detailsExpanded;
                  setDetailsExpanded(next);
                  if (chipsRef.current) chipsRef.current.style.maxHeight = next ? '800px' : '400px';
                }}
              />
              <ActionButton
                icon={Trash2}
                label="Löschen"
                danger
                disabled={isDeleting}
                onClick={handleDelete}
              />
            </div>

            {/* Metadaten - nur bei Details */}
            {detailsExpanded && <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mb-3">
              {/* Zeile 1: Erstellt */}
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/50 text-xs">Erstellt</span>
                <span className="text-white/90 text-sm">
                  {currentVideo.created_at
                    ? new Date(currentVideo.created_at + (String(currentVideo.created_at).includes('Z') || String(currentVideo.created_at).includes('+') ? '' : 'Z')).toLocaleString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'Gerade eben'}
                </span>
              </div>

              {/* Zeile 2: Kosten */}
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/50 text-xs">Kosten</span>
                <span className="text-white/90 text-sm">
                  {(currentVideo.cost_snaps !== undefined && currentVideo.cost_snaps !== null && currentVideo.cost_snaps > 0)
                    ? currentVideo.cost_snaps.toFixed(2) + ' Snaps'
                    : '—'}
                </span>
              </div>

              {/* Zeile 3: Video-Format */}
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/50 text-xs">Format</span>
                <span className="text-white/90 text-sm">
                  {currentVideo.video_aspect_ratio || '9:16'} {(currentVideo.video_aspect_ratio || '9:16') === '9:16' ? 'Story' : (currentVideo.video_aspect_ratio || '') === '16:9' ? 'Quer' : 'Feed'} · {currentVideo.video_resolution || '720p'}
                </span>
              </div>

              {/* Chips (wenn vorhanden) */}
              {currentVideo.motion_tags && currentVideo.motion_tags.length > 0 && (
                <div>
                  <span className="text-white/50 text-xs block mb-1.5">Effekte</span>
                  <div className="flex flex-wrap gap-1.5">
                    {currentVideo.motion_tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-white/10 rounded text-white/80 text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>}

            {/* Prompt (wenn vorhanden) - NUR User-Prompt, KEIN Agenten-Prompt */}
            {detailsExpanded && currentVideo.user_prompt && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mb-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-white/50 text-xs">Prompt</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(currentVideo.user_prompt).then(() => {
                        showToast('Prompt kopiert');
                      }).catch(() => {
                        // Fallback für ältere Browser
                        const ta = document.createElement('textarea');
                        ta.value = currentVideo.user_prompt;
                        ta.style.position = 'fixed';
                        ta.style.opacity = '0';
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                        showToast('Prompt kopiert');
                      });
                    }}
                    className="text-white/40 hover:text-white/80 transition-colors flex-shrink-0"
                    title="Prompt kopieren"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                  </button>
                </div>
                <p className="text-white/90 text-sm whitespace-pre-wrap break-words mt-1" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>{currentVideo.user_prompt}</p>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="px-4 mb-2 pt-4">
            <div
              ref={timelineRef}
              className="relative h-8 flex items-center cursor-pointer"
              style={{ touchAction: 'none' }}
            >
              <div className="relative w-full h-1 bg-white/20 rounded-full">
                <div
                  className="absolute h-full bg-white rounded-full"
                  style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                />
                {/* Scrub-Handle */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg"
                  style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%`, transform: 'translate(-50%, -50%)' }}
                />
              </div>
            </div>
            <div className="flex justify-between -mt-1">
              <span className="text-white/60 text-xs">{formatTime(currentTime)}</span>
              <span className="text-white/60 text-xs">{formatTime(duration)}</span>
            </div>
          </div>

          {/* Bottom Bar - Schließen + Counter */}
          <div className="flex items-center justify-center px-3 pb-safe relative">
            <button
              type="button"
              onTouchEnd={toggleChipsMenu}
              onClick={toggleChipsMenu}
              className="px-5 py-2 rounded-full bg-white/10 backdrop-blur-sm flex items-center gap-1.5 active:scale-95"
            >
              <ChevronDown className={`w-4 h-4 text-white transition-transform ${chipsExpanded ? 'rotate-180' : ''}`} />
              <span className="text-white text-sm font-medium">
                {chipsExpanded ? 'Schließen' : 'Aktionen'}
              </span>
            </button>

            {localVideos.length > 1 && (
              <div className="absolute right-3 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
                <span className="text-white text-sm font-medium">{currentIndex + 1} / {localVideos.length}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lösch-Bestätigung */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 flex items-center justify-center z-50 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 mx-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-white font-bold text-lg mb-2">Video löschen?</h3>
            <p className="text-white/60 text-sm mb-6">Dieses Video wird unwiderruflich gelöscht.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-all"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
};

// Action Button Component - wie bei ImageActionSheet
const ActionButton = ({ icon: Icon, label, onClick, onTouchEnd, danger = false, disabled = false, loading = false }) => (
  <button
    type="button"
    onClick={loading ? undefined : onClick}
    onTouchEnd={loading ? undefined : onTouchEnd}
    disabled={disabled || loading}
    style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
    className={`
      flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg backdrop-blur-sm transition-all
      ${(disabled || loading) ? 'opacity-70' : 'active:scale-[0.97]'}
      ${danger ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/90'}
    `}
  >
    {loading
      ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
      : <Icon className="w-4 h-4" />}
    <span className="text-[11px] font-medium">{label}</span>
  </button>
);

export default VideoActionSheet;
